export type LocationHierarchyItem = {
    level: number;
    name: string;
    iataCode?: string;
};

/**
 * Builds a human-readable label from a location hierarchy.
 *
 * The deepest node (the lane / PSOB) leads, with the terminal shown in
 * parentheses; any intermediate area (level 2) and the airport root are
 * omitted — the lane is "promoted" to the primary location. Level-0 locations
 * (airports) use their IATA code when available.
 *
 * @example
 * // Empty — returns empty string
 * buildLocationHierarchyLabel([])
 * // => ''
 *
 * @example
 * // Single level-0 location with IATA code
 * buildLocationHierarchyLabel([{ level: 0, name: 'John F. Kennedy', iataCode: 'JFK' }])
 * // => 'JFK'
 *
 * @example
 * // Single level-1 location (terminal)
 * buildLocationHierarchyLabel([{ level: 1, name: 'Terminal 1' }])
 * // => 'Terminal 1'
 *
 * @example
 * // Level-0 + level-1: no deep levels, so level-0 (IATA) is kept as parent
 * buildLocationHierarchyLabel([
 *     { level: 0, name: 'John F. Kennedy', iataCode: 'JFK' },
 *     { level: 1, name: 'Terminal 1' },
 * ])
 * // => 'Terminal 1 (JFK)'
 *
 * @example
 * // Deepest node leads, terminal in parens, intermediate area dropped
 * buildLocationHierarchyLabel([
 *     { level: 1, name: 'Terminal 1' },
 *     { level: 2, name: 'Line 2-3' },
 *     { level: 3, name: 'Line 3' },
 * ])
 * // => 'Line 3 (Terminal 1)'
 */
export function buildLocationHierarchyLabel(locationHierarchy: LocationHierarchyItem[]): string {
    const sorted = [...locationHierarchy].sort((a, b) => b.level - a.level);

    const hasDeepLevels = sorted.some((loc) => loc.level >= 2);
    const items = hasDeepLevels ? sorted.filter((loc) => loc.level > 0) : sorted;

    if (items.length === 0) {
        return '';
    }

    if (items.length === 1) {
        return getDisplayName(items[0]);
    }

    // Lead with the deepest node, terminal in parentheses; drop any intermediate area.
    return `${getDisplayName(items[0])} (${getDisplayName(items[items.length - 1])})`;
}

function getDisplayName(loc: LocationHierarchyItem): string {
    return loc.level === 0 ? (loc.iataCode ?? loc.name) : loc.name;
}
